import * as THREE from "three";

import halftoneVertexShader from "../../shaders/halftone/vertex.glsl";
import halftoneFragmentShader from "../../shaders/halftone/fragment.glsl";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default class Tentacle {
  constructor(scene, experience) {
    // Références aux modules principaux de l'application
    this.experience = experience;
    this.scene = scene;

    this.resources = this.experience.resources;
    this.time = this.experience.time;
    this.sizes = this.experience.sizes;
    this.scroll = this.experience.scroll;
    this.debug = this.experience.debug;

    this.camera = this.experience.worldCamera;

    this.isVisible = true;
    // this.button = document.querySelector('button')

    // Propriétés de l'instance
    this.cursor = { x: 0, y: 0 };
    this.lastX = 0;
    this.lastY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.rotationZVelocity = 0;

    // this.mouse = {};
    // this.isDragging = false;
    // this.dragLastX = 0;
    // this.raycaster = new THREE.Raycaster();
    // this.mouseNDC = new THREE.Vector2();

    // this.rotationTarget = new THREE.Euler(); // La cible que le ressort essaie d'atteindre
    // this.rotationVelocity = new THREE.Vector3(); // La vitesse actuelle de l'animation ressort

    // Configuration des positions et échelles initiales
    this.scrollTweenY = null; // Pour stocker l'animation GSAP
    this.scrollTweenYPos = null; // Pour la position

    this.initialScale = { x: 1, y: 1, z: 1 };
    // this.initialPosition = { x: 0, y: -16, z: 0 };
    this.initialPosition = { x: 0, y: -18, z: 0 };
    this.finalPosition = { y: -20 };

    // this.initialRotation = { x: 0, y: 5 * Math.PI / 3 , z: 0 };
    this.initialRotation = { y: 4.25 };
    this.finalRotation = { y: -2 };

    this.container = new THREE.Group();

    // Initialisation du modèle
    this.resource = this.resources.items.tentacleModel;
    if (this.resource) {
      this.setModel();
      this.setAnimations();
      this.initEvents();
    } else {
      console.error("Tentacle model resource not found!");
    }

    // Debug
    if (this.debug.active) {
      this.setDebug();
    }
  }

  setModel() {
    this.model = this.resource.scene;

    const boundingBox = new THREE.Box3();
    boundingBox.setFromObject(this.model);

    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    this.model.scale.set(1.0, 1.0, 1.0);
    // this.model.position.copy(this.initialPosition);
    this.container.rotation.y = this.initialRotation.y;
    this.container.position.y = this.initialPosition.y;

    this.container.add(this.model);
    this.scene.add(this.container);

    const materialParameters = {
      color: "#2D0037",
      shadowColor: "#6F008A",
      lightColor: "#E099F1",
    };

    this.material = new THREE.ShaderMaterial({
      vertexShader: halftoneVertexShader,
      fragmentShader: halftoneFragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color(materialParameters.color)),
        uResolution: new THREE.Uniform(
          new THREE.Vector2(
            this.sizes.width * this.sizes.pixelRatio,
            this.sizes.height * this.sizes.pixelRatio
          )
        ),
        uTime: { value: 0.0 },

        // Uniforms pour l'ombre
        uShadowRepetitions: new THREE.Uniform(2),
        uShadowColor: new THREE.Uniform(
          new THREE.Color(materialParameters.shadowColor)
        ),
        uShadowDirection: new THREE.Uniform(new THREE.Vector3(0.0, -1.0, 1.0)),
        uShadowLow: new THREE.Uniform(-0.8),
        uShadowHigh: new THREE.Uniform(1.5),

        // Uniforms pour la lumière génerale
        uDirectionalLightColor: new THREE.Uniform(new THREE.Color("#ffffff")),
        uDirectionalLightIntensity: new THREE.Uniform(1.0),
        uDirectionalLightDirection: new THREE.Uniform(
          new THREE.Vector3(5.0, 5.0, 5.0)
        ),
        uDirectionalLightSpecular: new THREE.Uniform(0.1),

        // Uniforms pour la lumière
        uLightRepetitions: new THREE.Uniform(1),
        uLightColor: new THREE.Uniform(
          new THREE.Color(materialParameters.lightColor)
        ),
        uLightDirection: new THREE.Uniform(new THREE.Vector3(1.0, 1.0, 0.0)),
        uLightLow: new THREE.Uniform(0.5),
        uLightHigh: new THREE.Uniform(1.5),

        // Uniforms pour l'anim
        uSpeed: new THREE.Uniform(0.001),
        uAmplitude: new THREE.Uniform(2.0),
        uFrequency: new THREE.Uniform(0.15),
        uHeight: new THREE.Uniform(size.y),
        uWhipFactor: new THREE.Uniform(1.0),
      },
    });

    this.model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.material = this.material;
      }
    });
  }

  // Remplacez votre méthode setAnimations par ces DEUX méthodes

  setAnimations() {
    // Crée l'animation de scroll pour la rotation Y
    this.setYScrollAnimation();

    // Crée l'animation de scroll pour la position Y
    this.setYPositionAnimation();
  }

  setYScrollAnimation() {
    // Si une ancienne animation existe, on la détruit pour éviter les conflits
    if (this.scrollTweenY) {
      this.scrollTweenY.kill();
    }

    // On stocke la nouvelle animation dans this.scrollTweenY
    // Note: GSAP anime depuis la valeur actuelle, donc on force la valeur de départ
    this.container.rotation.y = this.initialRotation.y;

    this.scrollTweenY = gsap.to(this.container.rotation, {
      y: this.finalRotation.y,
      scrollTrigger: {
        trigger: "html",
        start: "top top",
        end: "bottom bottom",
        scrub: true,
      },
    });
  }

  setYPositionAnimation() {
    // Si une ancienne animation existe, on la détruit
    if (this.scrollTweenYPos) {
      this.scrollTweenYPos.kill();
    }

    // On force la position de départ
    this.container.position.y = this.initialPosition.y;

    // On stocke la nouvelle animation
    this.scrollTweenYPos = gsap.to(this.container.position, {
      y: this.finalPosition.y,
      scrollTrigger: {
        trigger: "html",
        start: "top top",
        end: "bottom bottom",
        scrub: true,
      },
    });
  }

  toggleVisibility() {
    this.isVisible = !this.isVisible;
    this.container.visible = this.isVisible;
  }

  initEvents() {
    window.addEventListener("mousemove", (event) => {
      this.cursor.x = event.clientX / this.sizes.width - 0.5;
      this.cursor.y = event.clientY / this.sizes.height - 0.5;

      // this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      // this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      // if (this.isDragging) {
      // 1. Calculer le delta (le déplacement)
      // const deltaX = event.clientX - this.dragLastX;

      // 2. Appliquer le delta à la rotation du container
      // Le 0.01 est un facteur de sensibilité, à ajuster selon tes goûts
      // this.container.position.x += deltaX * 0.001;

      // 3. Mettre à jour la dernière position pour le prochain mouvement
      // this.dragLastX = event.clientX;
      // }
    });

    // this.button.addEventListener("click", (e) => {
    //   this.toggleVisibility();
    // });

    // Écouteur pour DÉMARRER le glissement
    // window.addEventListener("mousedown", (event) => {
    //   // On met à jour le raycaster avec la dernière position de la souris
    //   this.raycaster.setFromCamera(this.mouseNDC, this.camera.instance);
    //   const intersects = this.raycaster.intersectObject(this.container, true);

    //   // Si le rayon touche notre container
    //   if (intersects.length > 0) {
    //     this.isDragging = true;
    //     // On initialise la position de départ du glissement
    //     this.dragLastX = event.clientX;
    //   }
    // });

    // Écouteur pour ARRETER le glissement
    // window.addEventListener("mouseup", () => {
    //   this.isDragging = false;
    // });
  }

  // La méthode update, appelée par son parent (World.js)
  update() {
    if (!this.model) return;

    // Met à jour l'uniforme du temps pour les shaders
    if (this.material) {
      this.material.uniforms.uTime.value = this.time.elapsed;
    }

    const parallaxX = this.cursor.x;
    this.container.position.z *= parallaxX;
    // const oscillationX = Math.sin(this.time.elapsed * 0.0008) * 0.05;
    // this.model.position.x +=
    //   (parallaxX * 2 + oscillationX - this.model.position.x) * 0.025;

    // // const rotationOscillationX = Math.sin(this.time.elapsed * 0.0008) * 0.05;
    // // this.model.rotation.x = this.initialRotation.x + rotationOscillationX;

    // this.currentX = this.cursor.x;
    // this.vitesseX = this.lastX - this.currentX;
    // this.lastX = this.currentX;

    // this.currentY = this.cursor.y;
    // this.vitesseY = this.lastY - this.currentY;
    // this.lastY = this.currentY;

    // const rotationZ = this.vitesseX * 1.0;
    // const rotationX = this.vitesseY * 1.0;

    // this.container.rotation.z +=
    //   (-rotationZ - this.container.rotation.z) * 0.01;
    //     gsap.to(this.container.rotation, {
    //     y: -rotationZ, // Ta cible
    //     duration: 0.5, // Durée pour atteindre la cible (à ajuster)
    //     ease: 'back.out(1.7)' // La fonction d'easing ! C'est là qu'est la magie.
    // });

    // Valeurs à définir une fois (à ajuster pour le feeling)

    // // Calcul pour l'axe Z
    // const forceZ = (-rotationZ - this.container.rotation.z) * STIFFNESS;
    // this.rotationZVelocity += forceZ;
    // this.rotationZVelocity *= DAMPING; // Appliquer la friction
    // this.container.rotation.z += this.rotationZVelocity;

    // this.container.rotation.x +=
    //   (-rotationX - this.container.rotation.x) * 0.01;

    // const STIFFNESS = 0.1; // La "raideur" du ressort
    // const DAMPING = 0.9; // La friction (entre 0 et 1)

    // if (!this.isDragging) {
    //   // CIBLE = 0
    //   const forceZ = (0 - this.container.position.x) * STIFFNESS;
    //   this.rotationZVelocity += forceZ;
    //   this.rotationZVelocity *= DAMPING;
    //   this.container.position.x += this.rotationZVelocity;
    // }
  }

  setDebug() {
    if (this.debug && this.debug.ui) {
      const mainFolder = this.debug.ui.addFolder("Tentacle Shader");

      // --- Dossier pour l'animation de Scroll ---
      const scrollFolder = mainFolder.addFolder("Scroll Animation");

      scrollFolder
        .add(this.initialRotation, "y")
        .min(-Math.PI * 2)
        .max(Math.PI * 2)
        .step(0.01)
        .name("Initial Y Rotation")
        .onChange(() => {
          // On recrée l'animation avec les nouvelles valeurs
          this.setYScrollAnimation();
        });

      scrollFolder
        .add(this.finalRotation, "y")
        .min(-Math.PI * 2)
        .max(Math.PI * 2)
        .step(0.01)
        .name("Final Y Rotation")
        .onChange(() => {
          // On recrée l'animation avec les nouvelles valeurs
          this.setYScrollAnimation();
        });

      // --- AJOUTEZ CES CONTRÔLES POUR LA POSITION ---
      scrollFolder
        .add(this.initialPosition, "x")
        .min(-30)
        .max(30)
        .step(0.1)
        .name("Initial X Position")
        .onChange(() => {
          // Note: la position X n'est pas animée par le scroll,
          // donc on la met à jour directement.
          this.container.position.x = this.initialPosition.x;
        });

      scrollFolder
        .add(this.initialPosition, "y")
        .min(-30)
        .max(30)
        .step(0.1)
        .name("Initial Y Position")
        .onChange(() => {
          this.setYPositionAnimation(); // On recrée l'animation de position
        });

      scrollFolder
        .add(this.initialPosition, "z")
        .min(-30)
        .max(30)
        .step(0.1)
        .name("Initial Z Position")
        .onChange(() => {
          // Idem que pour X
          this.container.position.z = this.initialPosition.z;
        });

      scrollFolder
        .add(this.finalPosition, "y")
        .min(-30)
        .max(30)
        .step(0.1)
        .name("Final Y Position")
        .onChange(() => {
          this.setYPositionAnimation(); // On recrée l'animation de position
        });

      // --- Dossier pour l'animation de l'onde (Wave) ---
      const waveFolder = mainFolder.addFolder("Wave Animation");

      waveFolder
        .add(this.material.uniforms.uFrequency, "value")
        .min(0)
        .max(10)
        .step(0.01)
        .name("frequency");

      waveFolder
        .add(this.material.uniforms.uAmplitude, "value")
        .min(0)
        .max(2)
        .step(0.01)
        .name("amplitude");

      waveFolder
        .add(this.material.uniforms.uSpeed, "value")
        .min(0)
        .max(5)
        .step(0.001)
        .name("speed");

      waveFolder
        .add(this.material.uniforms.uWhipFactor, "value")
        .min(0)
        .max(20)
        .step(0.01)
        .name("whip");

      // --- Dossier pour l'ombre ---
      const shadowFolder = mainFolder.addFolder("Shadow Halftone");

      const shadowParams = {
        color: this.material.uniforms.uShadowColor.value.getStyle(),
      };
      shadowFolder.addColor(shadowParams, "color").onChange(() => {
        this.material.uniforms.uShadowColor.value.set(shadowParams.color);
      });
      shadowFolder
        .add(this.material.uniforms.uShadowRepetitions, "value")
        .min(0)
        .max(100)
        .step(1)
        .name("repetitions");
      shadowFolder
        .add(this.material.uniforms.uShadowLow, "value")
        .min(-2.0)
        .max(2.0)
        .step(0.01)
        .name("low");
      shadowFolder
        .add(this.material.uniforms.uShadowHigh, "value")
        .min(-2.0)
        .max(2.0)
        .step(0.01)
        .name("high");
      shadowFolder
        .add(this.material.uniforms.uShadowDirection.value, "x")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction X");
      shadowFolder
        .add(this.material.uniforms.uShadowDirection.value, "y")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction Y");
      shadowFolder
        .add(this.material.uniforms.uShadowDirection.value, "z")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction Z");

      // --- Dossier pour la lumière ---
      const lightFolder = mainFolder.addFolder("Light Halftone");

      const lightParams = {
        color: this.material.uniforms.uLightColor.value.getStyle(),
      };
      lightFolder.addColor(lightParams, "color").onChange(() => {
        this.material.uniforms.uLightColor.value.set(lightParams.color);
      });
      lightFolder
        .add(this.material.uniforms.uLightRepetitions, "value")
        .min(0)
        .max(100)
        .step(1)
        .name("repetitions");
      lightFolder
        .add(this.material.uniforms.uLightLow, "value")
        .min(-2.0)
        .max(2.0)
        .step(0.01)
        .name("low");
      lightFolder
        .add(this.material.uniforms.uLightHigh, "value")
        .min(-2.0)
        .max(2.0)
        .step(0.01)
        .name("high");
      lightFolder
        .add(this.material.uniforms.uLightDirection.value, "x")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction X");
      lightFolder
        .add(this.material.uniforms.uLightDirection.value, "y")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction Y");
      lightFolder
        .add(this.material.uniforms.uLightDirection.value, "z")
        .min(-1)
        .max(1)
        .step(0.01)
        .name("direction Z");

      // --- Dossier pour la Lumière Directionnelle ---
      const directionalLightFolder = mainFolder.addFolder("Directional Light");

      const dlParams = {
        color: this.material.uniforms.uDirectionalLightColor.value.getStyle(),
      };
      directionalLightFolder.addColor(dlParams, "color").onChange(() => {
        this.material.uniforms.uDirectionalLightColor.value.set(dlParams.color);
      });

      directionalLightFolder
        .add(this.material.uniforms.uDirectionalLightIntensity, "value")
        .min(0)
        .max(5)
        .step(0.01)
        .name("intensity");

      directionalLightFolder
        .add(this.material.uniforms.uDirectionalLightSpecular, "value")
        .min(0)
        .max(32)
        .step(0.1)
        .name("specular");

      directionalLightFolder
        .add(this.material.uniforms.uDirectionalLightDirection.value, "x")
        .min(-5)
        .max(5)
        .step(0.01)
        .name("direction X");
      directionalLightFolder
        .add(this.material.uniforms.uDirectionalLightDirection.value, "y")
        .min(-5)
        .max(5)
        .step(0.01)
        .name("direction Y");
      directionalLightFolder
        .add(this.material.uniforms.uDirectionalLightDirection.value, "z")
        .min(-5)
        .max(5)
        .step(0.01)
        .name("direction Z");
    }
  }

  destroy() {
    if (this.model) this.scene.remove(this.model);
    if (this.material) this.material.dispose();
    // Il faudrait aussi retirer le listener 'mousemove' ici pour un nettoyage parfait
  }
}
